if (!customElements.get('retailer-catalogue')) {
  customElements.define('retailer-catalogue', class extends HTMLElement {
    connectedCallback() {
      if (this.downloadHandler) return;
      this.downloadLink = this.querySelector('[data-catalogue-download]');
      this.status = this.querySelector('[data-catalogue-status]');
      this.downloadHandler = this.download.bind(this);
      this.downloadLink.addEventListener('click', this.downloadHandler);
      this.reader = this.querySelector('[data-catalogue-reader]');
      this.viewport = this.querySelector('[data-catalogue-viewport]');
      this.pagesContainer = this.querySelector('[data-catalogue-pages]');
      this.pagesContainer.replaceChildren();
      this.reader.hidden = true;
      this.zoomOut = this.querySelector('[data-zoom-out]');
      this.zoomIn = this.querySelector('[data-zoom-in]');
      this.zoomFit = this.querySelector('[data-zoom-fit]');
      this.zoomLevel = this.querySelector('[data-zoom-level]');
      this.zoom = 1;
      this.generation = 0;
      this.renderTasks = new Set();
      this.events = new AbortController();
      this.zoomOut.addEventListener('click', () => this.setZoom(this.zoom - 0.25), { signal: this.events.signal });
      this.zoomIn.addEventListener('click', () => this.setZoom(this.zoom + 0.25), { signal: this.events.signal });
      this.zoomFit.addEventListener('click', () => this.setZoom(1), { signal: this.events.signal });
      this.loadDocument();
    }

    disconnectedCallback() {
      this.downloadLink?.removeEventListener('click', this.downloadHandler);
      this.downloadHandler = null;
      this.request?.abort();
      this.events?.abort();
      this.pageObserver?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.resizeTimer);
      this.generation++;
      this.renderTasks?.forEach(task => task.cancel());
      this.loadingTask?.destroy();
    }

    async loadDocument() {
      this.status.textContent = 'Kataloget indlæses…';
      try {
        // PDF.js 6.4.299 (Apache-2.0), hosted with the theme; loaded only on catalogue pages.
        const pdfjs = await import(this.dataset.libraryUrl);
        if (!this.isConnected) return;
        pdfjs.GlobalWorkerOptions.workerSrc = this.dataset.workerUrl;
        this.loadingTask = pdfjs.getDocument({ url: this.dataset.pdfUrl, isEvalSupported: false });
        this.document = await this.loadingTask.promise;
        this.pages = [];
        for (let number = 1; number <= this.document.numPages; number++) {
          const pdfPage = await this.document.getPage(number);
          if (!this.isConnected) return;
          const size = pdfPage.getViewport({ scale: 1 });
          const element = document.createElement('figure');
          element.className = 'retailer-catalogue__page';
          element.setAttribute('aria-label', `Katalogside ${number}`);
          this.pagesContainer.append(element);
          this.pages.push({ pdfPage, size, element, number });
        }
        this.maxPageWidth = Math.max(...this.pages.map(page => page.size.width));
        this.reader.hidden = false;
        this.status.textContent = '';
        this.lastWidth = this.viewport.clientWidth;
        this.layoutPages();
        this.resizeObserver = new ResizeObserver(() => {
          if (this.viewport.clientWidth === this.lastWidth) return;
          this.lastWidth = this.viewport.clientWidth;
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.layoutPages(), 120);
        });
        this.resizeObserver.observe(this.viewport);
      } catch (error) {
        if (!this.isConnected) return;
        this.reader.hidden = true;
        this.status.textContent = 'Kataloget kunne ikke vises her. Du kan stadig åbne eller downloade PDF-filen.';
      }
    }

    setZoom(value) {
      if (!this.pages?.length) return;
      this.zoom = Math.min(3, Math.max(0.5, value));
      this.layoutPages();
    }

    layoutPages() {
      if (!this.isConnected || !this.pages?.length) return;
      const scrollRatio = this.viewport.scrollTop / Math.max(1, this.viewport.scrollHeight);
      this.generation++;
      this.pageObserver?.disconnect();
      this.renderTasks.forEach(task => task.cancel());
      this.renderTasks.clear();
      this.scale = Math.max(1, this.viewport.clientWidth - 32) / this.maxPageWidth * this.zoom;
      for (const page of this.pages) {
        page.element.style.width = `${page.size.width * this.scale}px`;
        page.element.style.height = `${page.size.height * this.scale}px`;
        page.rendered = false;
        page.element.replaceChildren();
      }
      this.zoomLevel.textContent = `${Math.round(this.zoom * 100)} %`;
      this.zoomOut.disabled = this.zoom <= 0.5;
      this.zoomIn.disabled = this.zoom >= 3;
      this.zoomFit.disabled = false;
      this.pageObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const page = this.pages.find(item => item.element === entry.target);
          if (!page) continue;
          page.visible = entry.isIntersecting;
          if (page.visible && !page.rendered) this.renderPage(page);
          if (!page.visible) {
            page.element.querySelector('canvas')?.remove();
            if (!page.task) page.rendered = false;
          }
        }
      }, { root: this.viewport, rootMargin: '300px' });
      this.pages.forEach(page => this.pageObserver.observe(page.element));
      this.viewport.scrollTop = scrollRatio * this.viewport.scrollHeight;
    }

    async renderPage(page) {
      page.rendered = true;
      const generation = this.generation;
      const canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      // Limit high-DPI canvas memory on mobile; off-screen pages are rendered on demand.
      const cssPixels = page.size.width * page.size.height * this.scale * this.scale;
      const outputScale = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(4000000 / cssPixels));
      const view = page.pdfPage.getViewport({ scale: this.scale * outputScale });
      canvas.width = Math.ceil(view.width);
      canvas.height = Math.ceil(view.height);
      const task = page.pdfPage.render({ canvasContext: canvas.getContext('2d'), viewport: view });
      page.task = task;
      this.renderTasks.add(task);
      try {
        await task.promise;
        if (generation !== this.generation || !this.isConnected || !page.visible) return;
        page.element.append(canvas);
        const text = await page.pdfPage.getTextContent();
        if (generation !== this.generation || !this.isConnected) return;
        const accessibleText = document.createElement('div');
        accessibleText.className = 'visually-hidden';
        accessibleText.textContent = text.items.map(item => item.str || '').join(' ');
        page.element.querySelector('.visually-hidden')?.remove();
        page.element.append(accessibleText);
      } catch (error) {
        if (error.name !== 'RenderingCancelledException' && generation === this.generation && this.isConnected) {
          this.status.textContent = 'En side kunne ikke vises. Åbn PDF-filen i en ny fane for at læse hele kataloget.';
        }
      } finally {
        this.renderTasks.delete(task);
        if (page.task === task) {
          page.task = null;
          if (!page.visible) page.rendered = false;
        }
      }
    }

    async download(event) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.downloadLink.getAttribute('aria-busy') === 'true') return;

      this.downloadLink.setAttribute('aria-busy', 'true');
      this.status.textContent = 'PDF hentes…';
      this.request = new AbortController();

      try {
        // Shopify files may use a different origin, where the download attribute alone is ignored.
        const response = await fetch(this.downloadLink.href, { signal: this.request.signal });
        if (!response.ok) throw new Error('PDF download failed');
        const url = URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = url;
        link.download = this.downloadLink.download;
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        this.status.textContent = 'PDF er klar til at blive gemt.';
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = 'Download kunne ikke startes. Brug “Åbn PDF i ny fane” og gem kataloget derfra.';
        }
      } finally {
        this.downloadLink.removeAttribute('aria-busy');
      }
    }
  });
}
